import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Search,
  Plus,
  Truck,
  Eye,
  Edit3,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Calendar,
  RotateCcw,
  Banknote,
  CreditCard,
  Phone,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import DateFilter from '../components/DateFilter';
import SearchableSelect from '../components/SearchableSelect';

const avatarColors = [
  'bg-emerald-100 text-emerald-700',
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

export default function Suppliers() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('suppliers'); // 'suppliers' | 'payments'
  const [suppliers, setSuppliers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editSup, setEditSup] = useState(null);
  const [deleteSup, setDeleteSup] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', phone: '', email: '', address: '', pending_amount: '0', advance_amount: '0', advance_payment_method: 'cash', notes: '' });

  // Payment / Refund modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [deletePayment, setDeletePayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    supplier_id: '',
    payment_type: 'paid', // 'paid' | 'refund_received'
    amount: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'cash',
    is_advance: false,
    notes: '',
  });

  useEffect(() => {
    loadSuppliers();
    loadPayments();
  }, []);

  const loadSuppliers = async () => {
    try {
      let url = '/suppliers?limit=500';
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await api.get(url);
      const list = Array.isArray(res) ? res : (res?.suppliers || []);
      setSuppliers(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load suppliers');
    }
  };

  const loadPayments = async () => {
    try {
      let url = '/supplier-payments?limit=500';
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await api.get(url);
      const list = Array.isArray(res) ? res : (res?.payments || []);
      setPayments(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load supplier payments');
    }
  };

  const filteredSuppliers = suppliers.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.phone && s.phone.includes(search)) ||
      (s.email && s.email.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredPayments = payments.filter((p) => {
    const sName = (p.supplier_name || '').toLowerCase();
    const notes = (p.notes || '').toLowerCase();
    const method = (p.payment_method || '').toLowerCase();
    const query = search.toLowerCase();
    return sName.includes(query) || notes.includes(query) || method.includes(query);
  });

  const resetFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    loadSuppliers();
    loadPayments();
  };

  const applyDateFilter = () => {
    loadSuppliers();
    loadPayments();
  };

  const openAdd = () => {
    setAddForm({ name: '', phone: '', email: '', address: '', pending_amount: '0', advance_amount: '0', advance_payment_method: 'cash', notes: '' });
    setShowAdd(true);
  };

  const saveNew = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) return;
    try {
      await api.post('/suppliers', { name: addForm.name, phone: addForm.phone, email: addForm.email, address: addForm.address, pending_amount: parseFloat(addForm.pending_amount) || 0, advance_amount: parseFloat(addForm.advance_amount) || 0, advance_payment_method: addForm.advance_payment_method, notes: addForm.notes });
      toast.success(Number(addForm.advance_amount) > 0 ? 'Supplier and advance payment added' : 'Supplier added');
      setShowAdd(false);
      loadSuppliers();
    } catch (err) {
      toast.error(err.message || 'Failed to add supplier');
    }
  };

  const updateOpeningAmount = (field, value) => {
    const otherField = field === 'pending_amount' ? 'advance_amount' : 'pending_amount';
    setAddForm((current) => ({
      ...current,
      [field]: value,
      [otherField]: Number(value) > 0 ? '0' : current[otherField],
    }));
  };

  const openEdit = (sup) => {
    setEditSup(sup);
    setEditForm({ ...sup });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/suppliers/${editForm.id}`, { name: editForm.name, phone: editForm.phone, email: editForm.email, address: editForm.address, notes: editForm.notes });
      toast.success('Supplier updated');
      setEditSup(null);
      loadSuppliers();
    } catch (err) {
      toast.error(err.message || 'Failed to update supplier');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/suppliers/${deleteSup.id}`);
      toast.success('Supplier deleted');
      setDeleteSup(null);
      loadSuppliers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete supplier');
    }
  };

  const openPaymentModal = (sup = null, type = 'paid') => {
    const bal = Number(sup?.balance || 0);
    const credit = Number(sup?.credit_balance || 0);
    const amount = type === 'refund_received'
      ? (credit > 0 ? String(credit) : '')
      : (bal > 0 ? String(bal) : '');

    setPaymentForm({
      supplier_id: sup ? String(sup.id) : '',
      payment_type: type,
      amount: amount,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: 'cash',
      is_advance: false,
      notes: type === 'refund_received' ? 'Advance refund returned by supplier' : '',
    });
    setShowPaymentModal(true);
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!paymentForm.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }

    try {
      const isRefund = paymentForm.payment_type === 'refund_received';
      const res = await api.post('/supplier-payments', {
        supplier_id: parseInt(paymentForm.supplier_id),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        payment_type: paymentForm.payment_type,
        is_advance: paymentForm.is_advance,
        amount: amount,
        notes: paymentForm.notes,
      });

      toast.success(res?.message || (isRefund ? 'Advance refund received successfully!' : 'Payment recorded successfully!'));
      setShowPaymentModal(false);
      loadSuppliers();
      loadPayments();
    } catch (err) {
      toast.error(err.message || 'Failed to record transaction');
    }
  };

  const confirmDeletePayment = async () => {
    if (!deletePayment) return;
    try {
      await api.delete(`/supplier-payments/${deletePayment.id}`);
      toast.success('Payment transaction deleted');
      setDeletePayment(null);
      loadSuppliers();
      loadPayments();
    } catch (err) {
      toast.error(err.message || 'Failed to delete payment');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="relative flex-1 min-w-[280px]">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={activeTab === 'suppliers' ? 'Search suppliers by name, phone...' : 'Search payment history...'}
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={() => openPaymentModal(null, 'refund_received')}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700 active:scale-[0.97]"
          >
            <RotateCcw className="h-4 w-4" /> Receive Advance Refund
          </button>
          <button
            onClick={() => openPaymentModal(null, 'paid')}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700 active:scale-[0.97]"
          >
            <Banknote className="h-4 w-4" /> Pay Supplier
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.97]"
          >
            <Plus className="h-4 w-4" /> New Supplier
          </button>
        </div>
      </div>

      <DateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onApply={applyDateFilter}
        onReset={resetFilters}
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('suppliers')}
          className={clsx(
            'flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all',
            activeTab === 'suppliers'
              ? 'border-emerald-500 text-emerald-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <Truck className="h-4 w-4" /> Suppliers Directory ({suppliers.length})
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={clsx(
            'flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all',
            activeTab === 'payments'
              ? 'border-emerald-500 text-emerald-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <CreditCard className="h-4 w-4" /> Payments & Advance Refunds ({payments.length})
        </button>
      </div>

      {/* TAB 1: SUPPLIERS DIRECTORY */}
      {activeTab === 'suppliers' && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-emerald-500" />
              <h2 className="text-base font-semibold text-gray-900">Suppliers</h2>
            </div>
            <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
              {suppliers.length} Suppliers
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Supplier Name</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Phone</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Email</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Address</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Pending</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Advance</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Note</th>
                  <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSuppliers.map((sup, idx) => {
                  const weOwe = Number(sup.balance || 0);
                  const theyOwe = Number(sup.credit_balance || 0);
                  return (
                    <tr key={sup.id} className="group transition-colors hover:bg-gray-50/50">
                      <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                        <div
                          onClick={() => navigate(`/suppliers/${sup.id}`)}
                          className="flex items-center gap-3 cursor-pointer group/name"
                        >
                          <div className={clsx('flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-transform group-hover/name:scale-105', avatarColors[idx % avatarColors.length])}>
                            {sup.name.charAt(0)}
                          </div>
                          <span className="text-sm font-semibold text-gray-900 group-hover/name:text-emerald-600 transition-colors">{sup.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-sm text-gray-700">
                          <Phone className="h-3.5 w-3.5 text-gray-400" />
                          {sup.phone || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{sup.email || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-[200px] truncate">{sup.address || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-red-600">
                        {weOwe > 0 ? `Rs. ${weOwe.toLocaleString('en-PK', { minimumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className={clsx('text-sm font-semibold', theyOwe > 0 ? 'text-emerald-600' : 'text-gray-400')}>
                          {theyOwe > 0 ? `Rs. ${theyOwe.toLocaleString('en-PK', { minimumFractionDigits: 2 })}` : '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 max-w-[150px] truncate">{sup.notes || '-'}</td>
                      <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {weOwe > 0 && (
                            <button
                              onClick={() => openPaymentModal(sup, 'paid')}
                              className="flex items-center gap-1 rounded-lg bg-emerald-50 border border-emerald-200 px-2 py-1 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-100"
                              title="Pay Supplier"
                            >
                              <Banknote className="h-3.5 w-3.5" /> Pay
                            </button>
                          )}
                          {theyOwe > 0 && (
                            <button
                              onClick={() => openPaymentModal(sup, 'refund_received')}
                              className="flex items-center gap-1 rounded-lg bg-blue-50 border border-blue-200 px-2 py-1 text-xs font-bold text-blue-700 transition-colors hover:bg-blue-100"
                              title="Receive Advance Refund from Supplier"
                            >
                              <RotateCcw className="h-3.5 w-3.5" /> Refund
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/suppliers/${sup.id}`)}
                            className="rounded-lg p-2 text-blue-500 transition-colors hover:bg-blue-50 hover:text-blue-600"
                            title="View Supplier Details & Ledger"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button onClick={() => openEdit(sup)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600" title="Edit Profile">
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button onClick={() => setDeleteSup(sup)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredSuppliers.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Truck className="mb-3 h-12 w-12 text-gray-300" />
              <p className="text-sm">No suppliers found</p>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: PAYMENTS & REFUNDS */}
      {activeTab === 'payments' && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-500" />
              <h2 className="text-base font-semibold text-gray-900">Supplier Payment & Refund History</h2>
            </div>
            <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
              {payments.length} Transactions
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                  <th className="sticky-th-left px-6 py-3 whitespace-nowrap">Receipt / Date</th>
                  <th className="px-6 py-3 whitespace-nowrap">Supplier</th>
                  <th className="px-6 py-3 whitespace-nowrap">Type</th>
                  <th className="px-6 py-3 whitespace-nowrap">Method</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right">Amount</th>
                  <th className="px-6 py-3 whitespace-nowrap">Notes & Reference</th>
                  <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredPayments.map((p) => {
                  const isRefund = p.payment_type === 'refund_received';
                  return (
                    <tr key={p.id} className="group transition-colors hover:bg-gray-50/50">
                      <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-gray-900 text-sm">{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '-'}</div>
                        <div className="text-[11px] text-gray-400 font-mono">{isRefund ? `SRF-${p.id}` : `SPY-${p.id}`}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {p.supplier_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isRefund ? (
                          <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700">
                            Advance Refund Received
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
                            Payment Made
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold capitalize text-gray-700">
                          {p.payment_method || 'cash'}
                        </span>
                      </td>
                      <td className={clsx('px-6 py-4 whitespace-nowrap text-right font-extrabold text-sm', isRefund ? 'text-blue-600' : 'text-emerald-600')}>
                        {isRefund ? '+ ' : '- '}Rs. {Number(p.amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 max-w-xs truncate">
                        {p.notes || '-'}
                      </td>
                      <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => setDeletePayment(p)}
                          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          title="Delete Payment"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredPayments.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <CreditCard className="mb-3 h-12 w-12 text-gray-300" />
              <p className="text-sm">No payment records found</p>
            </div>
          )}
        </div>
      )}

      {/* ADD SUPPLIER MODAL */}
      {showAdd &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
            <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Add New Supplier</h3>
                <button onClick={() => setShowAdd(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveNew} className="flex min-h-0 flex-1 flex-col">
                <div className="overflow-y-auto px-6 py-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Company Name *</label>
                    <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
                  </div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone</label><input value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Email</label><input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  <div className="col-span-2"><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label><input value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Opening Pending Dues (Rs.)</label><input type="number" min="0" value={addForm.pending_amount} onChange={(e) => updateOpeningAmount('pending_amount', e.target.value)} disabled={Number(addForm.advance_amount) > 0} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-50 focus:border-emerald-500 focus:outline-none" /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance Given Now (Rs.)</label><input type="number" min="0" value={addForm.advance_amount} onChange={(e) => updateOpeningAmount('advance_amount', e.target.value)} disabled={Number(addForm.pending_amount) > 0} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-50 focus:border-emerald-500 focus:outline-none" /></div>
                  <div className="col-span-2 text-[11px] text-gray-500">Enter either opening pending dues or an advance—not both. Opening pending dues do not move cash; an advance is saved as a payment.</div>
                  {Number(addForm.advance_amount) > 0 && <div className="col-span-2"><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance Payment Method</label><div className="grid grid-cols-2 gap-2"><button type="button" onClick={() => setAddForm({ ...addForm, advance_payment_method: 'cash' })} className={clsx('rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors', addForm.advance_payment_method === 'cash' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50')}>Cash</button><button type="button" onClick={() => setAddForm({ ...addForm, advance_payment_method: 'card' })} className={clsx('rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors', addForm.advance_payment_method === 'card' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50')}>Card</button></div></div>}
                  <div className="col-span-2"><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Note</label><textarea value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" /></div>
                </div>
                </div>
                <div className="sticky bottom-0 mt-auto flex justify-end gap-3 border-t border-gray-100 bg-white px-6 py-4">
                  <button type="button" onClick={() => setShowAdd(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Supplier</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* EDIT MODAL */}
      {editSup &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditSup(null)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Supplier</h3>
                <button onClick={() => setEditSup(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveEdit} className="px-6 py-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2"><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Company Name</label><input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone</label><input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Email</label><input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  <div className="col-span-2"><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label><input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Pending (Rs.)</label><input type="number" value={editForm.balance || 0} className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-100 px-3 py-2.5 text-sm text-gray-500" disabled /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance (Rs.)</label><input type="number" value={editForm.credit_balance || 0} className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-100 px-3 py-2.5 text-sm text-gray-500" disabled /></div>
                  <p className="col-span-2 -mt-2 text-[11px] text-gray-500">Pending and advance can only change through payment or refund entries.</p>
                  <div className="col-span-2"><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Note</label><textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" /></div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setEditSup(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Changes</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE CONFIRM MODAL */}
      {deleteSup &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteSup(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Supplier?</h3>
              <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete <strong>{deleteSup.name}</strong>?</p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => setDeleteSup(null)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={confirmDelete} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* RECORD PAYMENT / RECEIVE REFUND MODAL */}
      {showPaymentModal &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPaymentModal(false)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {paymentForm.payment_type === 'refund_received' ? 'Receive Advance Refund from Supplier' : 'Make Supplier Payment'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {paymentForm.payment_type === 'refund_received'
                      ? 'Record refund returned by supplier into your cash/bank account'
                      : 'Record payment made to supplier'}
                  </p>
                </div>
                <button onClick={() => setShowPaymentModal(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Type selector */}
              <div className="mb-4 flex rounded-xl bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => {
                    const selSup = suppliers.find((s) => String(s.id) === String(paymentForm.supplier_id));
                    const bal = Number(selSup?.balance || 0);
                    setPaymentForm({
                      ...paymentForm,
                      payment_type: 'paid',
                      amount: bal > 0 ? String(bal) : '',
                      notes: paymentForm.notes === 'Advance refund returned by supplier' ? '' : paymentForm.notes,
                    });
                  }}
                  className={clsx(
                    'flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5',
                    paymentForm.payment_type === 'paid'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Banknote className="h-3.5 w-3.5" /> Pay Supplier (Cash Out)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const selSup = suppliers.find((s) => String(s.id) === String(paymentForm.supplier_id));
                    const credit = Number(selSup?.credit_balance || 0);
                    setPaymentForm({
                      ...paymentForm,
                      payment_type: 'refund_received',
                      amount: credit > 0 ? String(credit) : '',
                      notes: !paymentForm.notes ? 'Advance refund returned by supplier' : paymentForm.notes,
                    });
                  }}
                  className={clsx(
                    'flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5',
                    paymentForm.payment_type === 'refund_received'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Receive Refund (Cash In)
                </button>
              </div>

              {(() => {
                const selSup = suppliers.find((s) => String(s.id) === String(paymentForm.supplier_id));
                const bal = Number(selSup?.balance || 0);
                const credit = Number(selSup?.credit_balance || 0);

                if (paymentForm.payment_type === 'refund_received') {
                  return (
                    <div className="mb-4 rounded-xl bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Supplier Advance Credit:</span>
                        <span className="text-sm font-extrabold">Rs. {credit.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <p className="text-[11px] text-blue-700">
                        This refund receipt will <strong>add money to your pharmacy cash/bank account</strong> and settle/reduce what the supplier owes you.
                      </p>
                    </div>
                  );
                }

                if (selSup) {
                  return (
                    <div className={clsx(
                      'mb-4 rounded-xl border p-3 text-xs',
                      bal > 0 ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    )}>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Current Pending Dues:</span>
                        <span className="text-sm font-extrabold">Rs. {bal.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  );
                }

                return null;
              })()}

              <form onSubmit={handleSavePayment} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Supplier *</label>
                    <SearchableSelect
                      value={paymentForm.supplier_id ? String(paymentForm.supplier_id) : ''}
                      onChange={(val) => {
                        const selSup = suppliers.find((s) => String(s.id) === String(val));
                        const bal = Number(selSup?.balance || 0);
                        const credit = Number(selSup?.credit_balance || 0);
                        const autoAmount = paymentForm.payment_type === 'refund_received'
                          ? (credit > 0 ? String(credit) : '')
                          : (bal > 0 ? String(bal) : '');
                        setPaymentForm({
                          ...paymentForm,
                          supplier_id: val,
                          amount: autoAmount,
                        });
                      }}
                      options={suppliers.map((s) => {
                        const bal = Number(s.balance || 0);
                        const credit = Number(s.credit_balance || 0);
                        return {
                          value: String(s.id),
                          label: s.name,
                          sublabel: credit > 0
                            ? `Advance: Rs. ${credit.toLocaleString('en-PK', { minimumFractionDigits: 2 })}`
                            : bal > 0
                              ? `Pending: Rs. ${bal.toLocaleString('en-PK', { minimumFractionDigits: 2 })}`
                              : s.phone ? `Phone: ${s.phone}` : null,
                        };
                      })}
                      placeholder="-- Select Supplier --"
                      searchPlaceholder="Search supplier..."
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Date</label>
                    <input
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Method</label>
                    <div className="flex gap-2">
                      {['cash', 'card'].map((m) => (
                        <button
                          type="button"
                          key={m}
                          onClick={() => setPaymentForm({ ...paymentForm, payment_method: m })}
                          className={clsx(
                            'flex-1 py-2 text-xs font-semibold rounded-lg border transition-colors capitalize',
                            paymentForm.payment_method === m
                              ? paymentForm.payment_type === 'refund_received'
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-emerald-500 text-white border-emerald-500'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                          )}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Amount (Rs.) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      placeholder={(() => {
                        const selSup = suppliers.find((s) => String(s.id) === String(paymentForm.supplier_id));
                        const bal = Number(selSup?.balance || 0);
                        const credit = Number(selSup?.credit_balance || 0);
                        return paymentForm.payment_type === 'refund_received'
                          ? (credit > 0 ? `Max refund: ${credit}` : 'Enter amount')
                          : (bal > 0 ? `Total dues: ${bal}` : 'Enter amount');
                      })()}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                      required
                    />
                    {(() => {
                      const selSup = suppliers.find((s) => String(s.id) === String(paymentForm.supplier_id));
                      const bal = Number(selSup?.balance || 0);
                      const credit = Number(selSup?.credit_balance || 0);
                      if (paymentForm.payment_type === 'refund_received' && credit > 0) {
                        return (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => setPaymentForm({ ...paymentForm, amount: String(credit) })}
                              className="rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"
                            >
                              Full Advance (Rs. {credit.toLocaleString('en-PK', { minimumFractionDigits: 2 })})
                            </button>
                          </div>
                        );
                      }
                      if (paymentForm.payment_type === 'paid' && bal > 0) {
                        return (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => setPaymentForm({ ...paymentForm, amount: String(bal) })}
                              className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-100"
                            >
                              Full Dues (Rs. {bal.toLocaleString('en-PK', { minimumFractionDigits: 2 })})
                            </button>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                  <textarea
                    rows={2}
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder={paymentForm.payment_type === 'refund_received' ? 'Reason / notes for advance returned by supplier...' : 'Optional payment notes...'}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={clsx(
                      'flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors',
                      paymentForm.payment_type === 'refund_received'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-emerald-500 hover:bg-emerald-600'
                    )}
                  >
                    {paymentForm.payment_type === 'refund_received' ? (
                      <><RotateCcw className="h-4 w-4" /> Receive Refund & Deposit to Account</>
                    ) : (
                      <><Banknote className="h-4 w-4" /> Record Payment</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE PAYMENT CONFIRM */}
      {deletePayment &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeletePayment(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Payment?</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this payment of <strong>Rs. {(parseFloat(deletePayment.amount) || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}</strong> for <strong>{deletePayment.supplier_name}</strong>?
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => setDeletePayment(null)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={confirmDeletePayment} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
